import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { api, ApiError, type PredictionMatch } from '../api.js';
import AppHeader from '../components/AppHeader.js';
import PoolNav from '../components/PoolNav.js';
import Avatar from '../components/Avatar.js';
import TeamBadge from '../components/TeamBadge.js';
import { formatKickoffParts } from '../lib/intl.js';
import { pageMatches, pageSpans } from '../lib/matchPaging.js';
import CustomQuestionsSection from './CustomQuestionsSection.js';
import styles from './PredictionsScreen.module.css';

const STAGE_ORDER = [
  'group',
  'round-of-32',
  'round-of-16',
  'quarter-final',
  'semi-final',
  'third-place',
  'final',
];

/**
 * Competitions with a genuine stage taxonomy — group/knockout phases that
 * are each a distinct part of the tournament, not just "round N of a flat
 * season" — so a per-stage tab makes sense for them. Every flat league (La
 * Liga plus the 8 more added in Phase 10.26, and any future one) instead
 * gets the chronological view below: its rounds don't line up with kickoff
 * dates — round 1 alone can span nearly two weeks (La Liga 2026/27:
 * 2026-08-15 to 2026-08-27), so some clubs' round-2 game kicks off before
 * other clubs finish round 1, and a per-stage tab would either hide next
 * week's game behind last week's tab or split one calendar week across two
 * tabs.
 *
 * This list is deliberately the *short* one — new flat leagues need no
 * entry here, only a genuinely new stage-taxonomy competition does (a
 * second knockout tournament, say). Hardcoded rather than data-driven for
 * the same reason `STAGE_ORDER` above already is: this screen doesn't yet
 * fetch competition metadata, just `group.competitionSlug`.
 */
const STAGE_TAB_COMPETITIONS = new Set(['world-cup-2026', 'dev-fixtures']);

/**
 * How many matches one page of the chronological view shows.
 *
 * Only the chronological competitions need paging at all: a stage-filtered
 * competition is already chunked by its own tabs (the World Cup's biggest
 * stage is the 72-match group stage). A full La Liga season is 380 matches
 * in one flat list, and every one of them renders a `MatchCard` carrying two
 * score inputs, a save mutation and an avatar row — so this is about the DOM
 * and the scroll length, not just tidiness.
 *
 * 20 is one page ≈ two rounds (La Liga plays 10 matches per round), which
 * keeps a page's round headers meaningful rather than showing a sliver of
 * four different rounds.
 */
const PAGE_SIZE = 20;

/**
 * Plain chronological order, earliest first; undated matches last.
 *
 * Was "unfinished first, then finished newest-first", which read well as a
 * single 20-match list but breaks badly once paged over a whole season: every
 * finished match is exiled to the far end, so a match *vanished from page 1
 * the moment it ended* and reappeared 18 pages away. It also made the page
 * date ranges nonsense — the boundary page spanned "15 Aug 2026 – 30 May
 * 2027" because it mixed next May's fixtures with last August's results.
 *
 * Real time order fixes both: pages become contiguous date windows, last
 * week's results are one page *back* rather than at the opposite end, and
 * "what do I predict next" is still what you land on, because the initial
 * page is chosen to contain it (see `initialPageFor`) instead of being forced
 * to the top by the sort.
 */
function byKickoff(a: PredictionMatch, b: PredictionMatch): number {
  const aTime = a.kickoffAt ? Date.parse(a.kickoffAt) : null;
  const bTime = b.kickoffAt ? Date.parse(b.kickoffAt) : null;
  if (aTime === null && bTime === null) return 0;
  if (aTime === null) return 1;
  if (bTime === null) return -1;
  return aTime - bTime;
}

/**
 * The page to open on: the one holding the earliest match that hasn't
 * finished — i.e. whatever is in progress or up next. Falls back to the last
 * page once a season is over, since that's where the most recent results are.
 */
function initialPageFor(ordered: readonly PredictionMatch[], pageSize: number): number {
  const next = ordered.findIndex((m) => m.status !== 'finished');
  if (next === -1) return Math.max(0, Math.ceil(ordered.length / pageSize) - 1);
  return Math.floor(next / pageSize);
}

interface RoundGroup {
  stageSlug: string;
  stageName: string;
  matches: PredictionMatch[];
}

/**
 * Groups a date-sorted match list into contiguous same-round runs, each
 * getting its own header — the SofaScore fixture-list behavior the
 * chronological view is modeled on. Deliberately NOT "one section per round
 * regardless of position": a postponed round-1 match that resurfaces after
 * round 2 has already started gets a brand new "Round 1" header at that
 * later position rather than being folded back into the round-1 section
 * further up the list, since the list stays date-sorted first and a round
 * header is just a label over whatever run of same-round matches lands
 * next to each other in that order.
 */
function groupIntoRounds(matches: readonly PredictionMatch[]): RoundGroup[] {
  const groups: RoundGroup[] = [];
  for (const match of matches) {
    const current = groups.at(-1);
    if (current && current.stageSlug === match.stageSlug) {
      current.matches.push(match);
    } else {
      groups.push({ stageSlug: match.stageSlug, stageName: match.stageName, matches: [match] });
    }
  }
  return groups;
}

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

/** What's being typed for one match, before (or instead of) a save. */
interface Draft {
  home: string;
  away: string;
  penaltyWinner: 'home' | 'away' | null;
}

function draftFromMatch(match: PredictionMatch): Draft {
  return {
    home: match.prediction ? String(match.prediction.home) : '',
    away: match.prediction ? String(match.prediction.away) : '',
    penaltyWinner: match.prediction?.penaltyWinner ?? null,
  };
}

function MatchCard({
  match,
  groupId,
  poolId,
  locale,
  hideStagePill = false,
  allowLockForever,
}: {
  match: PredictionMatch;
  groupId: string;
  poolId: string;
  locale: string;
  /** Omit the stage pill when a round-group header above the card already says it. */
  hideStagePill?: boolean;
  /** See `bolaos.allowLockForever`'s doc comment. */
  allowLockForever: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Draft>(() => draftFromMatch(match));
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [revealOpen, setRevealOpen] = useState(false);
  const [revealHint, setRevealHint] = useState(false);
  const [pointsDetailsOpen, setPointsDetailsOpen] = useState(false);

  const lock = useMutation({
    mutationFn: () => api.lockPrediction(groupId, poolId, match.id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['predictions', groupId, poolId, locale] }),
  });

  // A save landing elsewhere (another tab, a refetch after a stage switch)
  // should not clobber a field the person is mid-typing, so this only resyncs
  // when the server's own value actually changed underneath the draft.
  useEffect(() => {
    setDraft(draftFromMatch(match));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deliberately narrow: see comment above.
  }, [match.prediction?.home, match.prediction?.away, match.prediction?.penaltyWinner]);

  const editable = match.editability.editable;
  // Server-enforced, not guessed: the API only ever populates `revealed` under
  // this exact condition (kickoff passed, finished, or locked), which is the
  // same set of reasons `editable` goes false for a participant. Read from
  // `editable` rather than re-deriving it, so the two can never disagree.
  const canReveal = !editable;
  const homeNum = Number.parseInt(draft.home, 10);
  const awayNum = Number.parseInt(draft.away, 10);
  const bothEntered =
    draft.home !== '' && draft.away !== '' && !Number.isNaN(homeNum) && !Number.isNaN(awayNum);
  const isLevel = bothEntered && homeNum === awayNum;
  const needsPenaltyPick = isLevel && match.canGoToPenalties;

  async function commit(penaltyWinner: 'home' | 'away' | null) {
    if (!bothEntered) return;
    if (needsPenaltyPick && !penaltyWinner) return; // Wait for the pick before saving.

    setSaveState('saving');
    try {
      await api.savePrediction(groupId, poolId, match.id, {
        home: homeNum,
        away: awayNum,
        penaltyWinner,
      });
      setSaveState('saved');
      setTimeout(() => setSaveState((s) => (s === 'saved' ? 'idle' : s)), 2500);
    } catch {
      setSaveState('error');
    }
  }

  const statusChip = (() => {
    if (match.status === 'postponed') {
      return { label: t('predictions.status.postponed'), className: styles.statusPostponed };
    }
    if (match.status === 'cancelled') {
      return { label: t('predictions.status.cancelled'), className: styles.statusPostponed };
    }
    if (match.status === 'finished') {
      return { label: t('predictions.status.finished'), className: styles.statusFinished };
    }
    if (!editable && match.editability.reason === 'locked_by_participant') {
      return { label: t('predictions.status.locked'), className: styles.statusLocked };
    }
    if (!editable && match.editability.reason === 'kickoff_passed') {
      return { label: t('predictions.status.inProgress'), className: styles.statusInProgress };
    }
    return null;
  })();

  const kickoffParts = formatKickoffParts(match.kickoffAt, locale);
  const isOffSchedule = match.status === 'postponed' || match.status === 'cancelled';

  // A kicked-off match reports a running score, so the banner below shows one
  // for `live` as well as `finished` — see the translator's `hasStarted`.
  // Guarded on the values actually being present rather than on status alone:
  // a match can flip to `live` a tick before its first score arrives.
  const isLive = match.status === 'live';
  const hasLiveOrFinalScore =
    (match.status === 'finished' || isLive) && match.realHome !== null && match.realAway !== null;

  return (
    <div className={`${styles.matchCard} ${!editable ? styles.matchCardLocked : ''}`}>
      <div className={styles.matchCardTopbar}>
        <div className={styles.topbarLeft}>
          {hideStagePill ? null : <span className={styles.stagePill}>{match.stageName}</span>}
          {statusChip ? (
            <span className={`${styles.statusChip} ${statusChip.className}`}>
              {statusChip.label}
            </span>
          ) : null}
        </div>
        {kickoffParts ? (
          <div
            className={`${styles.kickoffBlock} ${isOffSchedule ? styles.kickoffBlockMuted : ''}`}
          >
            <span className={styles.kickoffDate}>{kickoffParts.date}</span>
            <span className={styles.kickoffTime}>{kickoffParts.time}</span>
          </div>
        ) : null}
      </div>

      <div className={styles.matchCardBody}>
        <div className={styles.teams}>
          <div className={styles.team}>
            <TeamBadge team={match.homeTeam} />
            <span className={styles.teamName}>{match.homeTeam?.name ?? '—'}</span>
          </div>

          <div className={styles.scoreInputs}>
            <input
              type="number"
              inputMode="numeric"
              min={0}
              max={20}
              className={styles.scoreBox}
              aria-label={t('predictions.homeScoreLabel', { team: match.homeTeam?.name ?? '' })}
              value={draft.home}
              disabled={!editable}
              onChange={(e) => setDraft((d) => ({ ...d, home: e.target.value }))}
              onBlur={() =>
                void commit(isLevel && match.canGoToPenalties ? draft.penaltyWinner : null)
              }
            />
            <span className={styles.scoreSeparator}>×</span>
            <input
              type="number"
              inputMode="numeric"
              min={0}
              max={20}
              className={styles.scoreBox}
              aria-label={t('predictions.awayScoreLabel', { team: match.awayTeam?.name ?? '' })}
              value={draft.away}
              disabled={!editable}
              onChange={(e) => setDraft((d) => ({ ...d, away: e.target.value }))}
              onBlur={() =>
                void commit(isLevel && match.canGoToPenalties ? draft.penaltyWinner : null)
              }
            />
          </div>

          <div className={`${styles.team} ${styles.teamAway}`}>
            <span className={styles.teamName}>{match.awayTeam?.name ?? '—'}</span>
            <TeamBadge team={match.awayTeam} />
          </div>
        </div>

        {needsPenaltyPick && editable ? (
          <div className={styles.penaltyPrompt}>
            <span className={styles.penaltyPromptLabel}>{t('predictions.penaltyPrompt')}</span>
            <div className={styles.penaltyButtons}>
              <button
                type="button"
                className={`${styles.penaltyButton} ${draft.penaltyWinner === 'home' ? styles.penaltyButtonActive : ''}`}
                onClick={() => {
                  setDraft((d) => ({ ...d, penaltyWinner: 'home' }));
                  void commit('home');
                }}
              >
                {t('predictions.penaltyWinnerLabel', { team: match.homeTeam?.name ?? '' })}
              </button>
              <button
                type="button"
                className={`${styles.penaltyButton} ${draft.penaltyWinner === 'away' ? styles.penaltyButtonActive : ''}`}
                onClick={() => {
                  setDraft((d) => ({ ...d, penaltyWinner: 'away' }));
                  void commit('away');
                }}
              >
                {t('predictions.penaltyWinnerLabel', { team: match.awayTeam?.name ?? '' })}
              </button>
            </div>
          </div>
        ) : null}

        {/* Once the window closes (locked, kickoff passed, or finished) the
            interactive prompt above stops rendering entirely — but a
            previously-chosen shootout pick is still part of what was
            predicted, and must stay visible rather than silently vanishing. */}
        {!editable && match.prediction?.penaltyWinner ? (
          <p className={styles.penaltyPickReadout}>
            {t('predictions.penaltyWinnerLabel', {
              team:
                match.prediction.penaltyWinner === 'home'
                  ? match.homeTeam?.name
                  : match.awayTeam?.name,
            })}
          </p>
        ) : null}

        {/* The same banner serves a finished and an in-progress match, because
            it is the same fact — the score right now — and splitting it into
            two components would let the two drift apart. What must NOT be the
            same is how final it looks: a live score is provisional, so it is
            labelled "live", tinted with the in-progress hue and given a
            pulsing dot, rather than reading as a result. */}
        {hasLiveOrFinalScore ? (
          <div
            className={`${styles.realScoreBanner} ${isLive ? styles.realScoreBannerLive : ''}`}
            // Live scores arrive by polling, so the region has to announce
            // itself when it changes; a final score is already static by the
            // time it renders.
            aria-live={isLive ? 'polite' : undefined}
          >
            <span className={styles.realScoreLabel}>
              {isLive ? <span className={styles.livePulse} aria-hidden="true" /> : null}
              {isLive ? t('predictions.liveScore') : t('predictions.finalScore')}
            </span>
            <span className={styles.realScoreValue}>
              {match.realHome} × {match.realAway}
            </span>
          </div>
        ) : null}

        <p
          className={`${styles.saveStatus} ${saveState === 'saved' ? styles.saveStatusSaved : ''} ${saveState === 'error' ? styles.saveStatusError : ''}`}
        >
          {saveState === 'saving' ? t('predictions.saving') : null}
          {saveState === 'saved' ? t('predictions.saved') : null}
          {saveState === 'error' ? t('predictions.saveFailed') : null}
        </p>

        {/* How this match scores, independent of anyone's predictions or the
            reveal trade below it — public knowledge regardless of whether
            the match has kicked off, so it isn't gated behind `canReveal`
            the way seeing everyone's picks is. Mirrors a custom
            competition's `PointsDetails` (`CustomQuestionsSection.tsx`). */}
        <div className={styles.detailsWrapper}>
          <button
            type="button"
            className={styles.detailsToggle}
            onClick={() => setPointsDetailsOpen((o) => !o)}
          >
            {pointsDetailsOpen
              ? t('predictions.pointsDetailsHide')
              : t('predictions.pointsDetailsShow')}
          </button>

          {pointsDetailsOpen ? (
            <>
              <div className={styles.pointsLegend}>
                <span className={styles.legendItem}>
                  <span className={`${styles.legendSwatch} ${styles.swatchExact}`} />
                  {t('predictions.legendExact', { count: match.matchParams.exact })}
                </span>
                <span className={styles.legendItem}>
                  <span className={`${styles.legendSwatch} ${styles.swatchWinner}`} />
                  {t('predictions.legendWinner', { count: match.matchParams.outcome })}
                </span>
                <span className={styles.legendItem}>
                  <span className={`${styles.legendSwatch} ${styles.swatchProx}`} />
                  {t('predictions.legendProx', { count: match.matchParams.pot })}
                </span>
                <span className={styles.legendItem}>
                  <span className={`${styles.legendSwatch} ${styles.swatchPenalty}`} />
                  {t('predictions.legendPenalties', {
                    exact: match.matchParams.penaltyExact,
                    wrong: match.matchParams.penaltyWrong,
                  })}
                </span>
              </div>
              <p className={styles.pointsExplanation}>{t('predictions.pointsExplanation')}</p>
            </>
          ) : null}
        </div>

        {editable && match.prediction && allowLockForever ? (
          <div className={styles.lockRow}>
            <button
              type="button"
              className={styles.lockButton}
              disabled={lock.isPending}
              onClick={() => {
                if (window.confirm(t('predictions.lockConfirm'))) lock.mutate();
              }}
            >
              {lock.isPending ? t('predictions.locking') : t('predictions.lockForever')}
            </button>
          </div>
        ) : null}

        <div className={styles.revealRow}>
          <button
            type="button"
            className={`${styles.revealButton} ${!canReveal ? styles.revealButtonDisabled : ''}`}
            aria-disabled={!canReveal}
            title={canReveal ? undefined : t('predictions.revealHint')}
            onClick={() => {
              // Deliberately not a native `disabled` button: that would also
              // block the click on a phone, where there is no hover to show
              // the `title` tooltip first. Grayed out, but still clickable,
              // so tapping it is how a touch user discovers *why* it's inert.
              if (!canReveal) {
                setRevealHint(true);
                return;
              }
              setRevealHint(false);
              setRevealOpen((o) => !o);
            }}
          >
            {revealOpen && canReveal
              ? t('predictions.hidePredictions')
              : t('predictions.viewPredictions')}
          </button>
          {revealHint && !canReveal ? (
            <p className={styles.revealHint}>{t('predictions.revealHint')}</p>
          ) : null}
        </div>

        {revealOpen && canReveal ? (
          <div className={styles.revealed}>
            {match.revealed.length === 0 ? (
              <p className={styles.revealedEmpty}>{t('predictions.revealedEmpty')}</p>
            ) : (
              <ul className={styles.revealedList}>
                {match.revealed.map((r) => (
                  <li key={r.participantId} className={styles.revealedRow}>
                    <Avatar name={r.displayName} imageUrl={r.image} size={36} />
                    <span className={styles.revealedName}>{r.displayName}</span>
                    {r.score ? (
                      <>
                        <span className={styles.ptsBadge}>
                          {t('predictions.revealedPoints', { count: r.score.total })}
                        </span>
                        <span className={`${styles.ptBadge} ${styles.swatchExact}`}>
                          {r.score.exact}
                        </span>
                        <span className={`${styles.ptBadge} ${styles.swatchWinner}`}>
                          {r.score.outcome}
                        </span>
                        <span className={`${styles.ptBadge} ${styles.swatchProx}`}>
                          {r.score.proximity}
                        </span>
                        <span className={`${styles.ptBadge} ${styles.swatchPenalty}`}>
                          {r.score.penalty}
                        </span>
                      </>
                    ) : null}
                    <span className={styles.revealedScore}>
                      {r.home} × {r.away}
                      {/* Just the team name here, not the full "wins on penalties"
                          sentence — this row is already packed with four pill
                          badges and a PTS badge; "See points details" above
                          already established what a penalty pick means. */}
                      {r.penaltyWinner
                        ? ` (${(r.penaltyWinner === 'home' ? match.homeTeam?.name : match.awayTeam?.name) ?? ''})`
                        : ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
      </div>

      {!editable && match.editability.reason !== 'match_finished' ? (
        <p className={styles.lockReason}>
          {t(`predictions.lockReason.${match.editability.reason}`)}
        </p>
      ) : null}

      {match.venue ? (
        <div className={styles.matchCardFooter}>
          <span>{match.venue}</span>
        </div>
      ) : null}
    </div>
  );
}

export default function PredictionsScreen() {
  const { t, i18n } = useTranslation();
  const { id = '', poolId = '' } = useParams();
  const locale = i18n.resolvedLanguage ?? 'en';
  const queryClient = useQueryClient();

  const [stageFilter, setStageFilter] = useState<string | null>(null);
  /** Index into the chronological view's pages; unused by stage-filtered competitions. */
  const [page, setPage] = useState(0);
  /**
   * Deliberately NOT persisted, unlike the theme. Hiding results is a
   * "tidy the screen right now" action, and a preference that outlived the
   * session would recreate the exact confusion this screen already caused
   * once — finished matches being absent with no visible reason why. Kept in
   * component state, so it lasts as long as the intent behind it.
   */
  const [hideFinished, setHideFinished] = useState(false);

  const { data: pool } = useQuery({
    queryKey: ['pool', id, poolId],
    queryFn: () => api.getPool(id, poolId),
  });

  // DEV-ONLY: "Lock forever" is deliberately irreversible in the real API —
  // that's the whole mechanic (PLAN.md 5.5) — which makes it painful to test
  // by hand without a way back. `import.meta.env.DEV` means Vite strips this
  // block (and the button it renders) from a production build entirely, and
  // the API route it calls is itself excluded outside development too (see
  // routes/predictions.ts) — but delete this block along with that route
  // before this app is ever pointed at real production data, rather than
  // relying on either guard alone.
  const devUnlockAll = useMutation({
    mutationFn: () => api.unlockAllPredictionsDev(id, poolId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['predictions', id, poolId, locale] }),
  });
  // Custom-competition bolões (PLAN.md Phase 10.3/10.4) have no ruleset, so
  // the football endpoint below would 400 for them — each query stays
  // disabled until `pool` has loaded and confirmed which sport this is.
  const isCustom = pool?.sportId === 'custom';
  const { data, isPending, isError, error } = useQuery({
    queryKey: ['predictions', id, poolId, locale],
    queryFn: () => api.getPredictions(id, poolId, locale),
    enabled: !!pool && !isCustom,
    /**
     * Poll only while a match is actually in progress, so the live score on
     * screen doesn't sit frozen at whatever it was when the page opened
     * (`staleTime` alone never refetches an open screen).
     *
     * Conditional rather than a blanket interval: a season is 380 matches and
     * is idle almost all of the time, so polling always would be traffic for
     * nothing on all but a few hours a week. This hits our own API, not
     * football-data — the provider is only ever called by the scheduler, so
     * no amount of viewers can affect the 10-calls/minute budget. A minute is
     * comfortably finer-grained than the 2-minute sync tick that feeds it.
     */
    refetchInterval: (query) =>
      query.state.data?.matches.some((m) => m.status === 'live') ? 60_000 : false,
  });
  const customQuestions = useQuery({
    queryKey: ['questions', id, poolId],
    queryFn: () => api.getQuestions(id, poolId),
    enabled: !!pool && isCustom,
  });

  const chronological = pool ? !STAGE_TAB_COMPETITIONS.has(pool.competitionSlug) : false;

  // Computed here rather than after the loading guards below, so the effect
  // that picks the opening page can see it. Memoised because sorting a full
  // 380-match season on every keystroke in a score box is real work.
  const orderedMatches = useMemo(() => {
    if (!data) return [];
    if (!chronological) return data.matches.filter((m) => m.stageSlug === stageFilter);
    const sorted = [...data.matches].sort(byKickoff);
    return hideFinished ? sorted.filter((m) => m.status !== 'finished') : sorted;
  }, [data, chronological, stageFilter, hideFinished]);

  /**
   * How many the "hide finished" control would take away — both what decides
   * whether that control is worth rendering at all, and the number it shows
   * while they're hidden, so nobody has to wonder what they're missing.
   * Counted off `data`, not `orderedMatches`, since the latter is exactly the
   * list they've been filtered out of.
   */
  const finishedCount = useMemo(
    () => (chronological ? (data?.matches.filter((m) => m.status === 'finished').length ?? 0) : 0),
    [data, chronological],
  );

  const stages = data
    ? STAGE_ORDER.filter((slug) => data.matches.some((m) => m.stageSlug === slug))
    : [];
  // Translated stage names come from the matches themselves (server-resolved,
  // DB-backed as of Phase 7) rather than a client-side lookup table.
  const stageNameBySlug = new Map(data?.matches.map((m) => [m.stageSlug, m.stageName]) ?? []);

  // Default to the earliest stage that still has an unplayed match, so a
  // returning player lands on what's next rather than back at match 1.
  // Irrelevant (and skipped) for a chronological competition, which has no
  // stage tabs to default.
  useEffect(() => {
    if (chronological || stageFilter !== null || !data) return;
    const nextUp = stages.find((slug) =>
      data.matches.some((m) => m.stageSlug === slug && m.status !== 'finished'),
    );
    setStageFilter(nextUp ?? stages[0] ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, chronological]);

  // The chronological equivalent of the stage default above: open on the page
  // holding what's in progress or up next, rather than on August when it's
  // February. Runs once per bolão — guarded by a ref rather than by `page`'s
  // own value, since page 0 is a legitimate place for someone to have paged
  // back to and re-defaulting them mid-session would fight the controls.
  const openingPageChosen = useRef(false);
  // Re-anchor on a different bolão, and whenever the finished filter flips —
  // removing (or restoring) a dozen matches renumbers every page under the
  // reader, so holding the old index would land them somewhere arbitrary.
  // Declared before the effect below so it clears the flag first.
  useEffect(() => {
    openingPageChosen.current = false;
  }, [poolId, hideFinished]);
  useEffect(() => {
    if (!chronological || openingPageChosen.current || orderedMatches.length === 0) return;
    openingPageChosen.current = true;
    setPage(initialPageFor(orderedMatches, PAGE_SIZE));
  }, [chronological, orderedMatches]);

  if (!pool || (isCustom && customQuestions.isPending)) {
    return (
      <main className="screen">
        <p className="muted">{t('common.loading')}</p>
      </main>
    );
  }

  if (isCustom) {
    return (
      <main className="screen">
        <AppHeader groupId={id} poolId={poolId} />
        <PoolNav groupId={id} poolId={poolId} />
        {customQuestions.isError ? (
          <p className="error">
            {customQuestions.error instanceof ApiError
              ? customQuestions.error.message
              : t('common.somethingWentWrong')}
          </p>
        ) : customQuestions.data ? (
          <CustomQuestionsSection
            groupId={id}
            poolId={poolId}
            locale={locale}
            data={customQuestions.data}
          />
        ) : null}
      </main>
    );
  }

  if (isPending) {
    return (
      <main className="screen">
        <p className="muted">{t('common.loading')}</p>
      </main>
    );
  }

  if (isError) {
    return (
      <main className="screen">
        <PoolNav groupId={id} poolId={poolId} />
        <p className="error">
          {error instanceof ApiError ? error.message : t('common.somethingWentWrong')}
        </p>
      </main>
    );
  }

  // Paging applies to the chronological view only — a stage-filtered
  // competition is already chunked by its own tabs. See PAGE_SIZE.
  const {
    pageCount,
    safePage,
    pageStart,
    matches: pagedMatches,
  } = pageMatches(orderedMatches, page, PAGE_SIZE);
  const visibleMatches = chronological ? pagedMatches : orderedMatches;
  const spans = chronological ? pageSpans(orderedMatches, PAGE_SIZE) : [];
  // One formatter for the whole picker rather than one per option.
  const spanFormat = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' });

  return (
    <main className="screen">
      <AppHeader groupId={id} poolId={poolId} />
      <PoolNav groupId={id} poolId={poolId} />

      {/* The bolão's name and the tab bar are `PoolNav`'s job now, so this
          block is only here for the dev-only unlock control. */}
      {import.meta.env.DEV ? (
        <button
          type="button"
          className={styles.devButton}
          style={{ alignSelf: 'flex-start' }}
          disabled={devUnlockAll.isPending}
          onClick={() => {
            if (window.confirm(t('predictions.devUnlockAllConfirm'))) devUnlockAll.mutate();
          }}
        >
          {devUnlockAll.isPending ? t('predictions.devUnlocking') : t('predictions.devUnlockAll')}
        </button>
      ) : null}

      {!data.isParticipant ? (
        <div className="panel" style={{ padding: 'var(--space-4)' }}>
          <p>{t('predictions.notParticipant')}</p>
          <p className="muted" style={{ fontSize: '0.85rem' }}>
            {t('predictions.notParticipantHint')}
          </p>
        </div>
      ) : null}

      {chronological ? null : (
        <div className={styles.stageFilters}>
          {stages.map((slug) => (
            <button
              key={slug}
              type="button"
              className={`${styles.stageFilterBtn} ${slug === stageFilter ? styles.stageFilterBtnActive : ''}`}
              onClick={() => setStageFilter(slug)}
            >
              {stageNameBySlug.get(slug) ?? slug}
            </button>
          ))}
        </div>
      )}

      {/* Offered only where results and fixtures actually share a list. The
          stage-filtered view is already scoped to one phase of a competition,
          and for a finished tournament like World Cup 2026 this control could
          only ever empty the screen. */}
      {/* Only worth showing once there is more than one page — a two-round
          rehearsal season shouldn't grow a pager it doesn't need. */}
      {chronological && pageCount > 1 ? (
        <div className={styles.pagerTop}>
          <span className="muted">
            {t('predictions.pagination.showing', {
              from: pageStart + 1,
              to: pageStart + visibleMatches.length,
              total: orderedMatches.length,
            })}
          </span>
          <label className={styles.roundJump}>
            <span className="muted">{t('predictions.pagination.jumpLabel')}</span>
            <select
              className="input"
              value={safePage}
              onChange={(event) => {
                setPage(Number(event.target.value));
                window.scrollTo({ top: 0 });
              }}
            >
              {spans.map((span) => {
                // Page number first so the picker and the pager below name
                // the same thing, then the dates that page actually covers —
                // a range on its own gave no clue where you were in the
                // season. A page whose matches have no kickoff yet (seeded
                // before the provider published dates) still needs a
                // selectable label, so it falls back to the number alone.
                const label = t('predictions.pagination.pageNumber', { page: span.page + 1 });
                return (
                  <option key={span.page} value={span.page}>
                    {span.from && span.to
                      ? `${label} · ${spanFormat.formatRange(new Date(span.from), new Date(span.to))}`
                      : label}
                  </option>
                );
              })}
            </select>
          </label>
        </div>
      ) : null}

      {/* Sits directly above the first card, below the pager, and spans the
          column: this is a control everyone in a pool should find without
          hunting, and tucked in the corner of the pager row it read as part
          of the paging rather than as its own thing.

          Offered only where results and fixtures actually share a list. The
          stage-filtered view is already scoped to one phase of a competition,
          and for a finished tournament like World Cup 2026 this could only
          ever empty the screen. */}
      {chronological && finishedCount > 0 ? (
        <button
          type="button"
          className={`${styles.viewToggle} ${hideFinished ? styles.viewToggleActive : ''}`}
          aria-pressed={hideFinished}
          onClick={() => {
            setHideFinished((wasHidden) => !wasHidden);
            window.scrollTo({ top: 0 });
          }}
        >
          {hideFinished
            ? t('predictions.showFinished', { count: finishedCount })
            : t('predictions.hideFinished')}
        </button>
      ) : null}

      {visibleMatches.length === 0 ? (
        <p className="muted">{t('predictions.noMatches')}</p>
      ) : chronological ? (
        <div className={styles.matchList}>
          {groupIntoRounds(visibleMatches).map((group, index) => (
            <div key={`${group.stageSlug}-${index}`} className={styles.roundGroup}>
              <h2 className={styles.roundGroupHeader}>{group.stageName}</h2>
              {group.matches.map((match) => (
                <MatchCard
                  key={match.id}
                  match={match}
                  groupId={id}
                  poolId={poolId}
                  locale={locale}
                  hideStagePill
                  allowLockForever={pool?.allowLockForever ?? true}
                />
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.matchList}>
          {visibleMatches.map((match) => (
            <MatchCard
              key={match.id}
              match={match}
              groupId={id}
              poolId={poolId}
              locale={locale}
              allowLockForever={pool?.allowLockForever ?? true}
            />
          ))}
        </div>
      )}

      {chronological && pageCount > 1 ? (
        <div className={styles.pager}>
          <button
            type="button"
            className="button button-secondary"
            disabled={safePage === 0}
            // Back to the top on every page change: without it a page turn
            // leaves you mid-list looking at the pager you just clicked,
            // with the new page's first matches already scrolled past.
            onClick={() => {
              setPage(safePage - 1);
              window.scrollTo({ top: 0 });
            }}
          >
            {t('predictions.pagination.prev')}
          </button>
          <span className="muted">
            {t('predictions.pagination.position', { current: safePage + 1, total: pageCount })}
          </span>
          <button
            type="button"
            className="button button-secondary"
            disabled={safePage >= pageCount - 1}
            onClick={() => {
              setPage(safePage + 1);
              window.scrollTo({ top: 0 });
            }}
          >
            {t('predictions.pagination.next')}
          </button>
        </div>
      ) : null}
    </main>
  );
}
